import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { countBy, isEmpty, mapValues, omitBy, isUndefined } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { PusherEventName } from 'anchor-scout-common/types';
import { AiFilters, Box, TableList, Page, PageEmptyState, PageErrorState, FilterPickerGroup, PageContentHeader, Spinner, ConfirmationModal, Typography, DropdownMenu, ChipButton, Button, useStateInRef, DropdownMenuItem, useIsPullRefreshing } from 'seagull';
import { ChevronDown, ChevronUp, Download, EmailSent, Import, EditAI } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useAgreementRowActions } from '~/components/AgreementActions/useAgreementRowActions';
import { BulkAmendmentAgentPane } from '~/components/ai/BulkAmendmentAgentPane/BulkAmendmentAgentPane';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import TableFilters from '~/components/TableFilters/TableFilters';
import { recurrencePeriodsTextsPath } from '~/constants';
import { useOverlayContext, useIoContext, useStore, useStoreActions } from '~/contexts';
import { useAgreements } from '~/hooks/reactQuery/queries/useAgreements';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useBulkActionModal } from '~/hooks/useTerminateAgreement/useBulkActionModal';
import { useVerifyPlaidBankAccount } from '~/hooks/useVerifyPlaidBankAccount';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { relationshipModel, relationshipSummaryModel } from '~/models';
import { PlaidBankAccountStatusTypes } from '~/models/payment-method';
import { useSubscribeToBillingObservable } from '~/providers/BillingObservableProvider';
import { PaymentMethodEventType, PusherEvent, useBusinessChannelEvent } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import { useAppConfig, useStoreStatic } from '~/store/store-context';
import { helpArticles } from '~/utils/helpArticles';
import { buildRelationshipsParamsFilter } from '~/utils/relationships-params-filter-builder';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import SeeWhatItsLike from '../../components/SeeWhatItsLike/SeeWhatItsLike';
import { AgreementsAiSearch } from './AgreementsAiSearch/AgreementsAiSearch';
import { AgreementPredicateChips } from './AgreementsAiSearch/chips/AgreementPredicateChips';
import { useAgreementsAiSearchResults } from './AgreementsAiSearch/hooks/useAgreementsAiSearchResults';
import { useAgreementsAiSearchStore } from './AgreementsAiSearch/hooks/useAgreementsAiSearchStore';
import { filterToFixedControls } from './AgreementsAiSearch/materialize/filterToFixedControls.utils';
import styles from './AgreementsDashboard.module.scss';
import { getTableDefs } from './AgreementsDashboard.tableDefs';
import { sortAgreements } from './AgreementsDashboard.utils';
import { serializeFilter, filtersConfig, FilterType, deserializeFiltersFromURL, defaultAgreementStatusFilter, getTableFilterLabels, getTableFilterGroups, makeTableFilterConfig, formatFilterDescription, agreementFilterTextsPath } from './agreementsDashboardFilters';
import AgreementsDashboardRow from './AgreementsDashboardRow';
import { useBulkAmendmentAgentPane } from './hooks/useBulkAmendmentAgentPane';
import { NoAgreementsIcon } from './NoAgreementsIcon';
import img1 from './SeeWhatItIsLikeImages/1.png';
import img2 from './SeeWhatItIsLikeImages/2.png';
const columnHeadersTextsPath = 'pages.agreementsDashboard.columnHeaders';
export const AgreementsDashboardTestIds = pathgen('AgreementsDashboardTestIds');
export const GetAgreementsDashboardTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            AgreementsDashboardTID: {
                Root: 'agreements-dashboard',
                Empty: 'agreements-empty',
                EmptyNewButton: 'agreements-empty-new-btn',
                EmptyTable: 'agreements-empty-table',
                CloseImportModal: 'close-import-agreements-modal',
                FilterBar: 'agreements-dashboard-filter-bar',
                Search: 'agreements-dashboard-search',
                TableHeader: 'agreements-dashboard-table-header',
                TableLoading: 'agreements-table-loading',
                BulkCounter: 'agreement-dashboard-bulk-counter',
                BulkActions: 'agreement-dashboard-bulk-actions',
                BulkRequestPaymentMethod: 'agreement-dashboard-bulk-request-payment-method',
                BulkExportCsv: 'agreement-dashboard-bulk-export-csv'
            }
        }
    });
function AgreementsTableBody(param) {
    let { isLoading, loadingTestId, children } = param;
    const isPullRefreshing = useIsPullRefreshing();
    if (isLoading && !isPullRefreshing) {
        return /*#__PURE__*/ _jsx("div", {
            className: styles.loading,
            "data-testid": loadingTestId,
            children: /*#__PURE__*/ _jsx(Spinner, {})
        });
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
}
export function ActualAgreementsDashboard(props) {
    const { AgreementsDashboardTID } = useMemo(()=>GetAgreementsDashboardTID(), []);
    const { data: _data, isRefreshing, search, filters, onChangeFilters, refresh, onChangeSearch } = props;
    const [data, setData] = useState(_data);
    useEffect(()=>setData(_data), [
        _data
    ]);
    const overlayContext = useOverlayContext();
    const { browser: { openNewTab, downloadCsv } } = useIoContext();
    const isVendor = useStore(selectors.user.isVendor);
    const { inputDebounceTime } = useAppConfig();
    const { api, logBI } = useStoreActions();
    const isMobileDevice = useIsMobileForHomePage();
    const { AGREEMENTS_CSV_EXPORT_V2, WIP_AMENDMENTS = false, ENABLE_AGREEMENTS_AI_SEARCH = false, BULK_AGENTIC_AMENDMENTS = false } = useFeatureFlags();
    const aiBarActive = ENABLE_AGREEMENTS_AI_SEARCH && !isMobileDevice;
    const [exportingAllCsv, setExportingAllCsv] = useState(false);
    const [bulkExportingCsv, setBulkExportingCsv] = useState(false);
    const { onEditAgreement, onRenameAgreement, onChangeAssignee, onDuplicateAgreement, onTerminateAgreement, onDownloadPDF } = useAgreementRowActions({
        onTerminateSuccess: refresh,
        onUpdateSuccess: refresh
    });
    const verifyPlaidBankAccount = useVerifyPlaidBankAccount();
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11252, 0);
    const { doBulkAction } = useBulkActionModal();
    const { getState } = useStoreStatic();
    const businessId = useStore(selectors.user.businessId);
    const aiSearch = useAgreementsAiSearchResults({
        businessId: businessId !== null && businessId !== void 0 ? businessId : '',
        isVendor,
        enabled: aiBarActive && businessId != null
    });
    const usingAiResults = aiBarActive && aiSearch.isActive;
    const isLoadingResults = isRefreshing || usingAiResults && aiSearch.isFetching;
    const aiStoreFilter = useAgreementsAiSearchStore((s)=>s.filter);
    const aiStoreLastQuery = useAgreementsAiSearchStore((s)=>s.lastQuery);
    const aiStoreFilterEdited = useAgreementsAiSearchStore((s)=>s.filterEditedSinceLastSearch);
    const { t: agreementFilterTexts } = useLocaleKeys(agreementFilterTextsPath);
    const { t: agreementsDashboardTexts } = useLocaleKeys('pages.agreementsDashboard');
    const { t: columnHeadersTexts } = useLocaleKeys(columnHeadersTextsPath);
    const { t: recurrencePeriodsTexts } = useLocaleKeys(recurrencePeriodsTextsPath);
    const effectiveDateLabel = columnHeadersTexts.effectiveDate();
    const title = agreementsDashboardTexts.title();
    const { headerItems, columnDefs, sortOptions, defaultSort } = useMemo(()=>getTableDefs(isVendor, columnHeadersTexts, effectiveDateLabel, WIP_AMENDMENTS), [
        isVendor,
        WIP_AMENDMENTS
    ]);
    const sortIds = useMemo(()=>headerItems.map((item)=>item.id), [
        headerItems
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const [serviceNameOptions, setServiceNameOptions] = useState([]);
    const tableFilterConfig = useMemo(()=>makeTableFilterConfig({
            serviceNameOptions,
            agreementFilterTexts,
            recurrencePeriodsTexts,
            isVendor,
            aiVariant: aiBarActive
        }), [
        serviceNameOptions,
        isVendor,
        aiBarActive
    ]);
    const filterLabels = useMemo(()=>getTableFilterLabels(agreementFilterTexts.filterLabels, agreementFilterTexts.aiFilterLabels, aiBarActive), [
        aiBarActive
    ]);
    const filterGroups = useMemo(()=>getTableFilterGroups(agreementFilterTexts.filterGroupLabels, WIP_AMENDMENTS), [
        WIP_AMENDMENTS
    ]);
    const [tableSelectedFilters, setTableSelectedFilters] = useState(()=>{
        const initial = {};
        for (const type of Object.values(FilterType)){
            if (filtersConfig[type].hasValue(filters[type])) {
                initial[type] = filters[type];
            }
        }
        return initial;
    });
    const [selected, setSelected] = useState({});
    const getSelected = useStateInRef(selected);
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const agreements = useMemo(()=>{
        return sortAgreements({
            agreements: usingAiResults ? aiSearch.agreements : data,
            sort,
            isVendor,
            headerItems
        });
    }, [
        usingAiResults,
        aiSearch.agreements,
        data,
        sort,
        isVendor,
        headerItems
    ]);
    const showAmendmentAgent = BULK_AGENTIC_AMENDMENTS && aiBarActive && isVendor;
    const amendmentAgent = useBulkAmendmentAgentPane({
        agreements,
        selected,
        setSelected,
        selectedAgreementIds: getSelectedAgreements(),
        aiSearchScope: {
            filter: aiStoreFilter,
            lastQuery: aiStoreLastQuery,
            filterEdited: aiStoreFilterEdited,
            totalMatchCount: aiSearch.totalMatchCount,
            isNarrowingTable: usingAiResults
        },
        isVendor,
        refreshLanes: async ()=>{
            await handlePromise(Promise.all([
                refresh(),
                aiSearch.refresh()
            ]));
        }
    });
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: agreements,
        scrollElRef
    });
    const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
    useOnMount(()=>{
        if (businessId) {
            api.relationship.getServiceNames(businessId, isVendor, 'agreement').then((names)=>{
                setServiceNameOptions(names.map((n)=>({
                        label: n,
                        value: n
                    })));
            });
        }
    });
    useEffect(()=>{
        // this unselects any agreement that is not found in the newly fetched list of agreements
        if (!data || isEmpty(getSelected())) return;
        const ids = data.map((agreement)=>agreement.id);
        const newSelected = mapValues(getSelected(), (origValue, agreementId)=>ids.includes(agreementId) ? origValue : false);
        setSelected(newSelected);
    }, [
        data
    ]);
    const onToggleAllSelection = useCallback((selectAll)=>{
        setSelected(agreements.reduce((acc, param)=>{
            let { id } = param;
            return Object.assign(acc, {
                [id]: selectAll
            });
        }, {}));
    }, [
        agreements
    ]);
    function onVerifyPaymentMethod(agreement) {
        if (!agreement.fundingSource) return;
        if (agreement.fundingSource.isPlaidVerification()) {
            verifyPlaidBankAccount({
                fundingSourceId: agreement.fundingSource.id(),
                relationshipId: agreement.id,
                onVerify () {
                    // Optimistic UI.
                    const newData = data.map((r)=>r.id === agreement.id ? relationshipSummaryModel.setFundingSourcePlaidVerificationStatus(r, PlaidBankAccountStatusTypes.ManuallyVerified) : r);
                    setData(newData);
                },
                onVerifyFailed () {
                    // Optimistic UI.
                    const newData = data.map((r)=>r.id === agreement.id ? relationshipSummaryModel.setFundingSourcePlaidVerificationStatus(r, PlaidBankAccountStatusTypes.VerificationFailed) : r);
                    setData(newData);
                }
            });
        }
    }
    const onSortChange = (sort)=>{
        logBI(11250, {
            column: sort ? sortIds[sort.colIndex] : 'none'
        });
        changeSort(sort);
    };
    const onClearSearch = ()=>{
        onChangeSearch('');
        debouncedLogSearchToBI.onClear();
    };
    const handleTableFiltersRefresh = (newFilters, sort)=>{
        const amendments = newFilters[FilterType.HasAmendments];
        if (!WIP_AMENDMENTS && Array.isArray(amendments) && amendments.length > 1) {
            newFilters = _object_spread_props(_object_spread({}, newFilters), {
                [FilterType.HasAmendments]: []
            });
        }
        const changes = {};
        for (const type of Object.values(FilterType)){
            const hasNew = type in newFilters;
            if (!hasNew) {
                if (filtersConfig[type].hasValue(filters[type])) {
                    changes[type] = filtersConfig[type].getEmptyValue();
                }
            } else {
                changes[type] = newFilters[type];
            }
        }
        onChangeFilters(changes);
        changeSort(sort);
    };
    const handleSetTableSelectedFilters = (updater)=>{
        setTableSelectedFilters((prev)=>{
            const next = typeof updater === 'function' ? updater(prev) : updater;
            const amendments = next[FilterType.HasAmendments];
            if (!WIP_AMENDMENTS && Array.isArray(amendments) && amendments.length > 1) {
                return _object_spread_props(_object_spread({}, next), {
                    [FilterType.HasAmendments]: []
                });
            }
            return next;
        });
    };
    const applyAiFilters = (parsed)=>{
        const changes = {};
        const selected = {};
        for (const type of Object.values(FilterType)){
            if (type in parsed) {
                changes[type] = parsed[type];
                selected[type] = parsed[type];
            } else {
                changes[type] = filtersConfig[type].getEmptyValue();
            }
        }
        onChangeFilters(changes);
        handleSetTableSelectedFilters(selected);
    };
    useEffect(()=>{
        if (aiBarActive && aiStoreFilter != null) applyAiFilters(filterToFixedControls(aiStoreFilter));
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps -- WHY: re-materialize URL state only when the AI filter itself changes, not on every render
    }, [
        aiStoreFilter,
        aiBarActive
    ]);
    function getSelectedAgreements() {
        return Object.entries(selected).filter((param)=>{
            let [, isSelected] = param;
            return isSelected;
        }).map((param)=>{
            let [agreementId, _] = param;
            return agreementId;
        });
    }
    async function bulkRequestPaymentMethod(selectedAgreements) {
        logBI(11254, {
            count: selectedAgreements.length
        });
        await doBulkAction({
            bulkItemIds: selectedAgreements,
            confirmation: {
                title: agreementsDashboardTexts.requestPaymentMethod(),
                primaryButtonLabel: agreementsDashboardTexts.sendRequest(),
                content: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                'By selecting "Send Request", all clients of the selected',
                                ' ',
                                /*#__PURE__*/ _jsx("strong", {
                                    children: selectedAgreements.length
                                }),
                                " agreements will receive an email asking them to set up their payment details in Anchor."
                            ]
                        }),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: agreementsDashboardTexts.considerSendingPersonalMessage()
                        }),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(ExternalLinkButton, {
                            openInNewTab: true,
                            href: helpArticles.requestPaymentMethod(),
                            bi: {
                                name: 'agreements_request_payment_method_learn_more'
                            },
                            children: agreementsDashboardTexts.learnMoreRequestPm()
                        })
                    ]
                })
            },
            progressTitle: agreementsDashboardTexts.requestPaymentMethodProgressTitle(),
            actionName: 'sent',
            itemName: agreementsDashboardTexts.paymentMethodRequestItemName(),
            onConfirmed: ()=>{
                logBI(11255, {
                    count: selectedAgreements.length
                });
            },
            canProcessItem: (itemId)=>agreements.find((a)=>a.id === itemId && !relationshipModel.isTerminated(a)) != null,
            processSingleItem: (agreementId)=>handlePromise(api.relationship.requestPaymentMethod(agreementId)),
            onDone: (failedAgreementIds)=>{
                setSelected(failedAgreementIds.reduce((acc, agreementId)=>_object_spread_props(_object_spread({}, acc), {
                        [agreementId]: true
                    }), {}));
            }
        });
    }
    const openImportAgreementsModal = ()=>{
        overlayContext.showModal({
            biName: 'agreements_dashboard_agreements_dashboard_import_modal__confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'agreements_confirm'
                        },
                        secondaryButton: {
                            name: 'agreements_cancel'
                        }
                    },
                    textAlignCenter: false,
                    primaryButtonLabel: agreementsDashboardTexts.importModal.close(),
                    onPrimaryButtonClick: dismissModal,
                    onCloseButtonClick: dismissModal,
                    title: agreementsDashboardTexts.importModal.title(),
                    primaryButtonTestId: AgreementsDashboardTID.CloseImportModal(),
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: agreementsDashboardTexts.importModal.intro()
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                        openInNewTab: true,
                                        href: "https://forms.gle/Q9sMHXFcp5Fs866FA",
                                        bi: {
                                            name: 'import_agreements_complete_form'
                                        },
                                        children: agreementsDashboardTexts.importModal.completeForm()
                                    }),
                                    ' ',
                                    agreementsDashboardTexts.importModal.outro()
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                style: {
                                    marginTop: '16px'
                                },
                                children: agreementsDashboardTexts.importModal.note()
                            })
                        ]
                    })
                });
            }
        });
    };
    const exportToCsv = ()=>{
        const mgmtHost = selectors.mgmtHost(getState());
        const filterParams = buildRelationshipsParamsFilter({
            type: 'agreement',
            isVendor,
            search,
            filters
        });
        const cleanedFilterParams = omitBy(filterParams, isUndefined);
        const searchParams = new URLSearchParams(cleanedFilterParams).toString();
        openNewTab("".concat(mgmtHost, "/business/").concat(businessId, "/relationships-summary-mv/export-csv?").concat(searchParams));
    };
    async function exportAllAgreementsCsv() {
        if (exportingAllCsv) return;
        setExportingAllCsv(true);
        try {
            const csvString = await api.relationship.exportAllAgreementsCsv({
                search,
                filters
            });
            downloadCsv(csvString, "anchor_agreements_export_".concat(format(new Date(), 'yyyy-MM-dd'), ".csv"));
            logBI(11256, {
                name: 'agreements_dashboard_export_all_csv'
            });
        } catch (unused) {
            overlayContext.showErrorToast(agreementsDashboardTexts.exportError(), 'export-agreements-csv-error');
        } finally{
            setExportingAllCsv(false);
        }
    }
    async function bulkExportAgreementsCsv() {
        if (bulkExportingCsv) return;
        setBulkExportingCsv(true);
        const ids = getSelectedAgreements();
        try {
            const csvString = await api.relationship.exportAgreementsCsvByIds(ids);
            downloadCsv(csvString, "anchor_agreements_export_".concat(format(new Date(), 'yyyy-MM-dd'), ".csv"));
            logBI(11257, {
                name: 'agreements_dashboard_bulk_export_csv'
            });
        } catch (unused) {
            overlayContext.showErrorToast(agreementsDashboardTexts.exportError(), 'export-agreements-csv-error');
        } finally{
            setBulkExportingCsv(false);
        }
    }
    const bulkActions = isVendor && !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                className: styles.selectedItems,
                testId: AgreementsDashboardTID.BulkCounter(),
                children: numSelected > 0 ? agreementsDashboardTexts.agreementsSelected({
                    count: numSelected
                }) : ''
            }),
            /*#__PURE__*/ _jsxs(DropdownMenu, {
                onOpenChange: setBulkActionsOpen,
                target: /*#__PURE__*/ _jsx(ChipButton, {
                    bi: {
                        name: 'agreements_bulk_actions'
                    },
                    testId: AgreementsDashboardTID.BulkActions(),
                    suffixIcon: bulkActionsOpen ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {}),
                    highlight: bulkActionsOpen,
                    disabled: numSelected === 0,
                    children: agreementsDashboardTexts.bulkActions()
                }),
                children: [
                    showAmendmentAgent && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                        iconStart: /*#__PURE__*/ _jsx(EditAI, {
                            size: 20
                        }),
                        testId: AgreementsDashboardTID.BulkAmendWithAI(),
                        onClick: amendmentAgent.open,
                        bi: {
                            name: 'agreements_bulk_amend_with_ai'
                        },
                        children: agreementsDashboardTexts.bulkAmendWithAI()
                    }),
                    /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                        iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                        testId: AgreementsDashboardTID.BulkRequestPaymentMethod(),
                        onClick: ()=>bulkRequestPaymentMethod(getSelectedAgreements()),
                        disabled: !isVendor,
                        bi: {
                            name: 'bulk_request_payment_method'
                        },
                        children: agreementsDashboardTexts.requestPaymentMethod()
                    }),
                    AGREEMENTS_CSV_EXPORT_V2 && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                        bi: {
                            name: 'agreements_dashboard_bulk_export_csv'
                        },
                        iconStart: /*#__PURE__*/ _jsx(Import, {}),
                        testId: AgreementsDashboardTID.BulkExportCsv(),
                        disabled: bulkExportingCsv,
                        intent: Intent.ExportAgreementsToCsv,
                        onClick: ()=>bulkExportAgreementsCsv(),
                        children: agreementsDashboardTexts.bulkExport()
                    })
                ]
            })
        ]
    });
    const renderEmptyPage = ()=>/*#__PURE__*/ _jsx(Page, {
            header: title,
            loading: isLoadingResults,
            variant: "containedHeight",
            onRefresh: refresh,
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: AgreementsDashboardTID.Empty(),
                illustration: /*#__PURE__*/ _jsx(NoAgreementsIcon, {}),
                title: agreementsDashboardTexts.emptyState.title(),
                message: agreementsDashboardTexts.emptyState.message(),
                actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                            steps: [
                                {
                                    title: agreementsDashboardTexts.emptyState.step1Title(),
                                    subtitle: agreementsDashboardTexts.emptyState.step1Subtitle(),
                                    image: {
                                        src: img1,
                                        width: 1246,
                                        height: 748
                                    }
                                },
                                {
                                    title: agreementsDashboardTexts.emptyState.step2Title(),
                                    subtitle: agreementsDashboardTexts.emptyState.step2Subtitle(),
                                    image: {
                                        src: img2,
                                        width: 1246,
                                        height: 748
                                    }
                                }
                            ],
                            testId: AgreementsDashboardTID.Root()
                        }),
                        /*#__PURE__*/ _jsx(CreateProposalButton, {
                            testId: AgreementsDashboardTID.EmptyNewButton()
                        })
                    ]
                })
            })
        });
    const renderAgreementsPage = ()=>/*#__PURE__*/ _jsx(Page, {
            testId: AgreementsDashboardTID.Root(),
            header: title,
            ref: scrollElRef,
            onRefresh: refresh,
            actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(DropdownMenu, {
                        testId: AgreementsDashboardTestIds.MoreActions.Root(),
                        target: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'open_agreements_more_actions'
                            },
                            skin: "primaryOutline",
                            testId: AgreementsDashboardTestIds.MoreActions.Button(),
                            suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
                            children: agreementsDashboardTexts.moreActions()
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                testId: AgreementsDashboardTestIds.MoreActions.ImportAgreementsBtn(),
                                iconStart: /*#__PURE__*/ _jsx(Download, {}),
                                onClick: ()=>openImportAgreementsModal(),
                                bi: {
                                    name: 'import_agreements'
                                },
                                children: agreementsDashboardTexts.importAgreements()
                            }),
                            AGREEMENTS_CSV_EXPORT_V2 ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                intent: Intent.ExportAgreementsToCsv,
                                iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                testId: AgreementsDashboardTestIds.MoreActions.ExportAgreementsBtn(),
                                onClick: exportAllAgreementsCsv,
                                disabled: exportingAllCsv,
                                bi: {
                                    name: 'agreements_dashboard_export_all_csv'
                                },
                                children: agreementsDashboardTexts.exportAll()
                            }) : /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                intent: Intent.ExportAgreementsToCsv,
                                iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                testId: AgreementsDashboardTestIds.MoreActions.ExportAgreementsBtn(),
                                onClick: exportToCsv,
                                bi: {
                                    name: 'export_agreements'
                                },
                                children: agreementsDashboardTexts.exportAgreements()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CreateProposalButton, {}, "new-agreement-button")
                ]
            }),
            onScroll: onScroll,
            children: /*#__PURE__*/ _jsxs(TableList.Provider, {
                hasActionColumn: true,
                enableRowSelection: isVendor,
                columnDefs: columnDefs,
                sort: sort,
                selectedRows: selected,
                onSortChange: onSortChange,
                onSelectionChange: setSelected,
                numRows: agreements.length,
                numSelectedRows: numSelected,
                onToggleAllSelection: onToggleAllSelection,
                children: [
                    /*#__PURE__*/ _jsxs(PageContentHeader, {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.agreementsFilters,
                                children: aiBarActive ? /*#__PURE__*/ _jsx(AiFilters, {
                                    gap: 12,
                                    searchRowGap: 12,
                                    input: /*#__PURE__*/ _jsx(Box, {
                                        className: styles.aiSearchInput,
                                        children: /*#__PURE__*/ _jsx(AgreementsAiSearch, {
                                            testId: AgreementsDashboardTID.AiSearch(),
                                            onFiltersParsed: applyAiFilters
                                        })
                                    }),
                                    searchRowActions: bulkActions && /*#__PURE__*/ _jsx(Box, {
                                        className: styles.aiBulkActions,
                                        children: bulkActions
                                    }),
                                    chips: /*#__PURE__*/ _jsx(FilterPickerGroup, {
                                        flexWrap: true,
                                        testId: AgreementsDashboardTID.FilterBar(),
                                        children: /*#__PURE__*/ _jsx(AgreementPredicateChips, {
                                            testId: AgreementsDashboardTID.PredicateChips(),
                                            serviceNameOptions: serviceNameOptions,
                                            agreements: data,
                                            isVendor: isVendor,
                                            matchCount: agreements.length
                                        })
                                    })
                                }) : /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                                    flexWrap: true,
                                    testId: AgreementsDashboardTID.FilterBar(),
                                    children: [
                                        /*#__PURE__*/ _jsx(TableFilters, {
                                            testIdPrefix: "agreements-dashboard",
                                            search: {
                                                bi: {
                                                    name: 'agreements_search'
                                                },
                                                value: search,
                                                testId: AgreementsDashboardTID.Search(),
                                                registerHotkey: registerSearchBoxHotkey,
                                                debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
                                                placeholder: agreementsDashboardTexts.searchPlaceholder(),
                                                onChange: (event)=>{
                                                    const searchStr = event.target.value;
                                                    onChangeSearch(searchStr);
                                                    debouncedLogSearchToBI.onChange(searchStr);
                                                },
                                                onClear: onClearSearch
                                            },
                                            filterLabels: filterLabels,
                                            filterConfig: tableFilterConfig,
                                            filterGroups: filterGroups,
                                            selectedFilters: tableSelectedFilters,
                                            setSelectedFilters: handleSetTableSelectedFilters,
                                            onRefresh: handleTableFiltersRefresh,
                                            onInputFocus: ()=>setRegisterSearchBoxHotkey(false),
                                            onInputBlur: ()=>setRegisterSearchBoxHotkey(true),
                                            mobile: {
                                                formatFilterDescription: (filter, value)=>formatFilterDescription({
                                                        filterType: filter,
                                                        value,
                                                        agreementFilterTexts,
                                                        recurrencePeriodsTexts,
                                                        isVendor
                                                    }),
                                                sortOptions
                                            }
                                        }),
                                        bulkActions
                                    ]
                                })
                            }),
                            !aiBarActive && /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph1",
                                weight: "light",
                                testId: AgreementsDashboardTestIds.TableHeader.AgreementsCounter(),
                                className: styles.agreementsCounter,
                                children: agreementsDashboardTexts.agreementsCount({
                                    count: agreements.length
                                })
                            }),
                            /*#__PURE__*/ _jsx(TableList.Header, {
                                items: headerItems,
                                testId: AgreementsDashboardTID.TableHeader()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(AgreementsTableBody, {
                        isLoading: isLoadingResults,
                        loadingTestId: AgreementsDashboardTID.TableLoading(),
                        children: agreements.length ? paginatedData.map((agreement, index)=>/*#__PURE__*/ _jsx(AgreementsDashboardRow, {
                                agreement: agreement,
                                isVendor: isVendor,
                                index: index,
                                onVerifyPaymentMethod: onVerifyPaymentMethod,
                                onEditAgreement: onEditAgreement,
                                onDownloadPDF: onDownloadPDF,
                                onDuplicateAgreement: onDuplicateAgreement,
                                onTerminateAgreement: onTerminateAgreement,
                                onRenameAgreement: onRenameAgreement,
                                onChangeAssignee: onChangeAssignee
                            }, agreement.id)) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            illustration: /*#__PURE__*/ _jsx(NoAgreementsIcon, {}),
                            title: agreementsDashboardTexts.noResults.title(),
                            message: agreementsDashboardTexts.noResults.message(),
                            testId: AgreementsDashboardTID.EmptyTable()
                        })
                    })
                ]
            })
        });
    const hasActiveFilters = Object.values(FilterType).some((type)=>filtersConfig[type].hasValue(filters[type]));
    useBusinessChannelEvent({
        source: 'mgmt',
        eventName: PusherEvent.PaymentMethod,
        callback: (param)=>{
            let { payload } = param;
            if (payload.type === PaymentMethodEventType.PaymentMethodUpdated) {
                refresh();
            }
        }
    });
    useBusinessChannelEvent({
        source: 'scout',
        eventName: PusherEventName.RelationshipApproved,
        callback: ()=>{
            refresh();
        }
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            showAmendmentAgent && /*#__PURE__*/ _jsx(BulkAmendmentAgentPane, _object_spread({
                testId: AgreementsDashboardTID.BulkAmendmentAgentPane()
            }, amendmentAgent.paneProps)),
            data.length > 0 || hasActiveFilters || search.length || isRefreshing ? renderAgreementsPage() : renderEmptyPage()
        ]
    });
}
function AgreementsDashboardLegacy() {
    const { api } = useStoreActions();
    const { t: agreementsDashboardTexts } = useLocaleKeys('pages.agreementsDashboard');
    const title = agreementsDashboardTexts.title();
    const businessId = useStore(selectors.user.businessId);
    const isVendor = useStore(selectors.user.isVendor);
    const shouldRefresh = useRef(false);
    const refreshRef = useRef(()=>{});
    const { urlState, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        agreementStatus: {
            multi: true,
            applyDefaultOnlyOnMount: true,
            defaultValue: defaultAgreementStatusFilter
        },
        effectiveDate: {
            defaultValue: ''
        },
        lastBillingDate: {
            defaultValue: ''
        },
        paymentMethod: {
            multi: true
        },
        completion: {
            multi: true
        },
        recurrence: {
            multi: true
        },
        hasCredit: {
            multi: true
        },
        automatic: {
            multi: true
        },
        billingTrigger: {
            multi: true
        },
        serviceNames: {
            multi: true
        },
        servicePriceIncreaseState: {
            multi: true
        },
        hasAmendments: {
            multi: true
        },
        amendmentsStatus: {
            multi: true
        }
    });
    const { search } = urlState;
    const deserializedFilters = useMemo(()=>deserializeFiltersFromURL(urlState), [
        urlState
    ]);
    async function refreshData() {
        if (!businessId) return Promise.resolve([]);
        return api.relationship.getSummaries({
            businessId,
            type: 'agreement',
            isVendor,
            search,
            filters: deserializedFilters
        });
    }
    useSubscribeToBillingObservable(async ()=>{
        refreshRef.current();
    });
    useSetDocumentTitle(title);
    useEffect(()=>{
        if (shouldRefresh.current) refreshRef.current();
    }, [
        urlState
    ]);
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: ()=>refreshData(),
        renderLoader: ()=>/*#__PURE__*/ _jsx(PageLoadingState, {
                header: title,
                loading: true
            }),
        renderError: (param)=>{
            let { refresh } = param;
            return /*#__PURE__*/ _jsx(PageErrorState, {
                title: title,
                onRefresh: refresh,
                pullToRefresh: true
            });
        },
        renderChild: (param)=>{
            let { data, loading, refresh } = param;
            refreshRef.current = refresh;
            function refreshOnUrlStateChange() {
                shouldRefresh.current = true;
            }
            return /*#__PURE__*/ _jsx(ActualAgreementsDashboard, {
                data: data,
                isRefreshing: loading,
                refresh: refresh,
                search: search,
                filters: deserializedFilters,
                onChangeFilters: (changes)=>{
                    refreshOnUrlStateChange();
                    const serialized = {};
                    for (const [type, value] of Object.entries(changes)){
                        serialized[type] = serializeFilter(type, value);
                    }
                    changeUrlState(serialized, {
                        push: true
                    });
                },
                onChangeSearch: (search)=>{
                    refreshOnUrlStateChange();
                    changeUrlState({
                        search
                    });
                }
            });
        }
    });
}
function AgreementsDashboardV2() {
    const { t: agreementsDashboardTexts } = useLocaleKeys('pages.agreementsDashboard');
    const title = agreementsDashboardTexts.title();
    const { urlState, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        agreementStatus: {
            multi: true,
            applyDefaultOnlyOnMount: true,
            defaultValue: defaultAgreementStatusFilter
        },
        effectiveDate: {
            defaultValue: ''
        },
        lastBillingDate: {
            defaultValue: ''
        },
        paymentMethod: {
            multi: true
        },
        completion: {
            multi: true
        },
        recurrence: {
            multi: true
        },
        hasCredit: {
            multi: true
        },
        automatic: {
            multi: true
        },
        billingTrigger: {
            multi: true
        },
        serviceNames: {
            multi: true
        },
        servicePriceIncreaseState: {
            multi: true
        },
        hasAmendments: {
            multi: true
        },
        amendmentsStatus: {
            multi: true
        }
    });
    const { search } = urlState;
    const deserializedFilters = useMemo(()=>deserializeFiltersFromURL(urlState), [
        urlState
    ]);
    const { agreements, isLoading, isFetching, refresh: refetchAgreements, error } = useAgreements({
        search,
        filters: deserializedFilters,
        refreshOnHookMount: true
    });
    async function refresh() {
        await refetchAgreements();
        return null;
    }
    useSubscribeToBillingObservable(async ()=>{
        await refetchAgreements();
    });
    useSetDocumentTitle(title);
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            title: title,
            onRefresh: refetchAgreements,
            pullToRefresh: true
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: title,
            loading: true
        });
    }
    return /*#__PURE__*/ _jsx(ActualAgreementsDashboard, {
        data: agreements,
        isRefreshing: isFetching,
        refresh: refresh,
        search: search,
        filters: deserializedFilters,
        onChangeFilters: (changes)=>{
            const serialized = {};
            for (const [type, value] of Object.entries(changes)){
                serialized[type] = serializeFilter(type, value);
            }
            changeUrlState(serialized, {
                push: true
            });
        },
        onChangeSearch: (search)=>{
            changeUrlState({
                search
            });
        }
    });
}
export function AgreementsDashboard() {
    const { AGREEMENTS_CSV_EXPORT_V2 } = useFeatureFlags();
    if (AGREEMENTS_CSV_EXPORT_V2) {
        return /*#__PURE__*/ _jsx(AgreementsDashboardV2, {});
    }
    return /*#__PURE__*/ _jsx(AgreementsDashboardLegacy, {});
}
